import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Invoice,
  InvoiceStats,
  InvoiceStatus,
  InvoiceType,
  BrokerageQueueLead,
} from '@/types/invoice.types';
import { PaginatedResponse } from '@/types';
import { invoicesService } from '@/services/invoices.service';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InvoiceStatusBadge, getAllInvoiceStatuses } from '@/components/billing';

/**
 * Format amount in Indian currency format (lakhs/crores)
 */
const formatIndianCurrency = (amount: number): string => {
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const invoiceTypeOptions: { value: InvoiceType | ''; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'DEVELOPER', label: 'Developer' },
  { value: 'INDEPENDENT', label: 'Independent' },
  { value: 'BROKER_REFERRAL', label: 'Broker Referral' },
];

const getInvoiceTypeLabel = (type: InvoiceType): string => {
  const option = invoiceTypeOptions.find((o) => o.value === type);
  return option?.label || type;
};

export const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<InvoiceStats | null>(null);
  const [brokerageQueue, setBrokerageQueue] = useState<BrokerageQueueLead[]>([]);
  const [meta, setMeta] = useState<PaginatedResponse<Invoice>['meta'] | null>(null);

  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingQueue, setIsLoadingQueue] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>(
    (searchParams.get('status') as InvoiceStatus) || ''
  );
  const [typeFilter, setTypeFilter] = useState<InvoiceType | ''>(
    (searchParams.get('type') as InvoiceType) || ''
  );
  const [page, setPage] = useState(Number(searchParams.get('page')) || 1);

  const fetchInvoices = useCallback(async () => {
    try {
      setIsLoadingInvoices(true);
      setError(null);

      const filters: Record<string, string> = {};
      if (statusFilter) filters.status = statusFilter;
      if (typeFilter) filters.type = typeFilter;

      const response = await invoicesService.getAll(filters, page, 20);
      setInvoices(response.data?.data ?? []);
      setMeta(response.data?.meta ?? null);
    } catch (err) {
      setError('Failed to load invoices');
      console.error('Error fetching invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  }, [statusFilter, typeFilter, page]);

  const fetchStats = useCallback(async () => {
    try {
      setIsLoadingStats(true);
      const response = await invoicesService.getStats();
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching invoice stats:', err);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  const fetchBrokerageQueue = useCallback(async () => {
    try {
      setIsLoadingQueue(true);
      const response = await invoicesService.getBrokerageQueue();
      // API interceptor unwraps { success, data } - backend returns { leads: [...] }
      setBrokerageQueue(response.data?.leads || []);
    } catch (err) {
      console.error('Error fetching brokerage queue:', err);
    } finally {
      setIsLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchBrokerageQueue();
  }, [fetchStats, fetchBrokerageQueue]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [statusFilter, typeFilter, page, setSearchParams]);

  const handleFilterChange = (key: 'status' | 'type', value: string) => {
    if (key === 'status') {
      setStatusFilter(value as InvoiceStatus | '');
    } else {
      setTypeFilter(value as InvoiceType | '');
    }
    setPage(1);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setTypeFilter('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter || typeFilter;
  const statusOptions = [{ value: '' as const, label: 'All Statuses' }, ...getAllInvoiceStatuses()];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Billing</h1>
          <p className="text-secondary-600 mt-1">
            Manage invoices and track payments
          </p>
        </div>
        <Button
          onClick={() => navigate('/billing/invoices/new')}
          leftIcon={
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          }
        >
          Create Invoice
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Invoiced */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Total Invoiced</p>
              {isLoadingStats ? (
                <div className="h-6 w-24 bg-secondary-200 rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-lg font-semibold text-secondary-900">
                  {formatIndianCurrency(stats?.total_invoiced || 0)}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Total Received */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Total Received</p>
              {isLoadingStats ? (
                <div className="h-6 w-24 bg-secondary-200 rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-lg font-semibold text-green-600">
                  {formatIndianCurrency(stats?.total_received || 0)}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Outstanding */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg
                className="w-6 h-6 text-yellow-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Outstanding</p>
              {isLoadingStats ? (
                <div className="h-6 w-24 bg-secondary-200 rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-lg font-semibold text-yellow-600">
                  {formatIndianCurrency(stats?.total_outstanding || 0)}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Overdue */}
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm text-secondary-600">Overdue</p>
              {isLoadingStats ? (
                <div className="h-6 w-24 bg-secondary-200 rounded animate-pulse mt-1"></div>
              ) : (
                <p className="text-lg font-semibold text-red-600">
                  {formatIndianCurrency(stats?.overdue || 0)}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Brokerage Queue */}
      {!isLoadingQueue && brokerageQueue.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-secondary-900">
                Brokerage Queue
              </h2>
              <p className="text-sm text-secondary-600">
                Leads ready for invoicing (booked but not yet invoiced)
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {brokerageQueue.slice(0, 6).map((lead) => (
              <div
                key={lead.id}
                className="p-4 bg-secondary-50 rounded-lg border border-secondary-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-secondary-900">{lead.name}</p>
                    <p className="text-sm text-secondary-500">#{lead.lead_number}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/billing/invoices/new?lead_id=${lead.id}`)}
                  >
                    Invoice
                  </Button>
                </div>
                {lead.booking_value && (
                  <p className="mt-2 text-sm text-secondary-600">
                    Booking: {formatIndianCurrency(lead.booking_value)}
                  </p>
                )}
                {lead.booked_at && (
                  <p className="text-xs text-secondary-400 mt-1">
                    Booked on {formatDate(lead.booked_at)}
                  </p>
                )}
              </div>
            ))}
          </div>
          {brokerageQueue.length > 6 && (
            <p className="text-sm text-secondary-500 mt-4 text-center">
              And {brokerageQueue.length - 6} more leads waiting for invoicing
            </p>
          )}
        </Card>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-secondary-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3">
          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            {invoiceTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-secondary-600 hover:text-secondary-900 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoadingInvoices && (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            <p className="text-secondary-600">Loading invoices...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoadingInvoices && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">Failed to load invoices</h3>
          <p className="text-secondary-600 mb-4">{error}</p>
          <Button onClick={fetchInvoices}>Try Again</Button>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingInvoices && !error && invoices.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-secondary-100 mb-4">
            <svg className="w-8 h-8 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            {hasActiveFilters ? 'No invoices found' : 'No invoices yet'}
          </h3>
          <p className="text-secondary-600 mb-4">
            {hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Create your first invoice to get started'}
          </p>
          {!hasActiveFilters && (
            <Button onClick={() => navigate('/billing/invoices/new')}>Create Invoice</Button>
          )}
        </div>
      )}

      {/* Invoice Table */}
      {!isLoadingInvoices && !error && invoices.length > 0 && (
        <>
          <div className="bg-white rounded-xl border border-secondary-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-secondary-200">
                <thead className="bg-secondary-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Lead
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-secondary-200">
                  {invoices.map((invoice) => {
                    const paidAmount = invoice.payments.reduce((sum, p) => sum + p.amount, 0);
                    const outstandingAmount = invoice.total_amount - paidAmount;

                    return (
                      <tr
                        key={invoice.id}
                        onClick={() => navigate(`/billing/invoices/${invoice.id}`)}
                        className="hover:bg-secondary-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-secondary-900">
                            {invoice.invoice_number}
                          </p>
                          <p className="text-xs text-secondary-500">
                            {invoice.bill_to_name}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-secondary-900">{invoice.lead.name}</p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-secondary-600">
                            {getInvoiceTypeLabel(invoice.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm font-medium text-secondary-900">
                            {formatIndianCurrency(invoice.total_amount)}
                          </p>
                          {outstandingAmount > 0 && invoice.status !== 'CANCELLED' && (
                            <p className="text-xs text-red-500">
                              Due: {formatIndianCurrency(outstandingAmount)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <InvoiceStatusBadge status={invoice.status} size="sm" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500">
                          {formatDate(invoice.issued_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/billing/invoices/${invoice.id}`);
                            }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl border border-secondary-200 shadow-sm px-4 py-3">
              <div className="text-sm text-secondary-600">
                Showing {(meta.page - 1) * meta.limit + 1} to{' '}
                {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} invoices
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === meta.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceList;
